import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Form, Input, Button, Card, Checkbox, message, Typography, Collapse } from 'antd';
import { MobileOutlined, MailOutlined } from '@ant-design/icons';
import client from '../api/client';
import useAuthStore from '../stores/authStore';
import SmsCodeInput from '../components/SmsCodeInput';
import { AgreementModal, USER_AGREEMENT_TEXT, PRIVACY_POLICY_TEXT } from '../components/Agreements';
import type { User } from '@dtwin/shared';

export default function Register() {
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const login = useAuthStore((s) => s.login);
  const phoneNumber = Form.useWatch('phoneNumber', form) || '';
  // 协议弹窗受控状态：null=关闭；'user'=用户协议；'privacy'=隐私政策
  const [agreementModal, setAgreementModal] = useState<'user' | 'privacy' | null>(null);

  const onFinish = async (values: {
    phoneNumber: string;
    smsCode: string;
    displayName?: string;
    email?: string;
    agree: boolean;
  }) => {
    setLoading(true);
    try {
      const res = await client.post('/auth/sms/register', {
        phoneNumber: values.phoneNumber,
        smsCode: values.smsCode,
        displayName: values.displayName || undefined,
        email: values.email || undefined,
      });
      const { user, token } = res.data as { user: User; token: string };
      if (!token) {
        message.error('注册失败：服务器未返回令牌');
        return;
      }
      login(user, token);
      message.success('注册成功，欢迎加入 DTwince');
      navigate('/', { replace: true });
    } catch (err) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message
        || (err as Error)?.message
        || '注册失败';
      message.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0f0f1a 0%, #1a1a2e 100%)',
      }}
    >
      <Card
        title={
          <Typography.Title level={4} style={{ margin: 0, textAlign: 'center' }}>
            DTwince 云边智孪 · 注册
          </Typography.Title>
        }
        style={{ width: 420, background: '#1a1a2e', borderColor: '#333' }}
      >
        <Form form={form} onFinish={onFinish} size="large" initialValues={{ agree: true }}>
          <Form.Item
            name="phoneNumber"
            rules={[
              { required: true, message: '请输入手机号' },
              { pattern: /^1[3-9]\d{9}$/, message: '手机号格式不正确' },
            ]}
          >
            <Input prefix={<MobileOutlined />} placeholder="手机号" maxLength={11} />
          </Form.Item>
          <Form.Item name="smsCode" rules={[{ required: true, message: '请输入验证码' }]}>
            <SmsCodeInput scene="register" phoneNumber={phoneNumber} />
          </Form.Item>
          <Form.Item name="displayName">
            <Input placeholder="昵称（选填）" maxLength={32} />
          </Form.Item>
          <Collapse
            ghost
            items={[
              {
                key: 'more',
                label: '更多选项',
                children: (
                  <Form.Item
                    name="email"
                    rules={[{ type: 'email', message: '邮箱格式不正确' }]}
                  >
                    <Input prefix={<MailOutlined />} placeholder="邮箱（选填，仅作联系方式）" />
                  </Form.Item>
                ),
              },
            ]}
            style={{ marginBottom: 16 }}
          />
          <Form.Item
            name="agree"
            valuePropName="checked"
            rules={[
              {
                validator: (_, v) =>
                  v ? Promise.resolve() : Promise.reject(new Error('请先阅读并勾选用户协议')),
              },
            ]}
          >
            <Checkbox>
              我已阅读并同意{' '}
              {/* ⚠️ 点击链接打开弹窗：必须阻止事件冒泡，否则会触发 Checkbox 取消勾选 */}
              <a
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setAgreementModal('user');
                }}
              >
                《用户协议》
              </a>{' '}
              与{' '}
              <a
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setAgreementModal('privacy');
                }}
              >
                《隐私政策》
              </a>
            </Checkbox>
          </Form.Item>
          <Form.Item style={{ marginBottom: 8 }}>
            <Button type="primary" htmlType="submit" loading={loading} block>
              注册
            </Button>
          </Form.Item>
          <div style={{ textAlign: 'center' }}>
            已有账号？<a onClick={() => navigate('/login')}>直接登录</a>
          </div>
        </Form>
      </Card>
      <AgreementModal
        open={agreementModal === 'user'}
        title="《用户协议》"
        content={USER_AGREEMENT_TEXT}
        onCancel={() => setAgreementModal(null)}
      />
      <AgreementModal
        open={agreementModal === 'privacy'}
        title="《隐私政策》"
        content={PRIVACY_POLICY_TEXT}
        onCancel={() => setAgreementModal(null)}
      />
    </div>
  );
}